// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { PatientTriageStatus } from '@polarissleep/shared';
import { PATIENT_TRIAGE_STATUS } from '@polarissleep/shared';
import type { TreatmentStatusCode } from './treatment-status';

/**
 * High-level triage stages from the care journey.
 * These map from the more granular TreatmentStatusCode values.
 */
export type TriageStage =
  | 'intake'
  | 'hsat'
  | 'hsat-results'
  | 'treatment-setup'
  | 'followup-30'
  | 'followup-90'
  | 'followup-180'
  | 'annual-followup'
  | 'non-adherent';

/** Display labels for each triage stage */
export const TRIAGE_STAGE_LABELS: Record<TriageStage, string> = {
  intake: 'Intake',
  hsat: 'HSAT',
  'hsat-results': 'HSAT Results',
  'treatment-setup': 'Treatment Setup',
  'followup-30': 'Follow-up 30-Day',
  'followup-90': 'Follow-up 90-Day',
  'followup-180': 'Follow-up 180-Day',
  'annual-followup': 'Annual Follow-up',
  'non-adherent': 'Non-Adherent',
};

/**
 * Maps granular treatment status codes to high-level triage stages.
 * Multiple treatment statuses can map to the same triage stage.
 */
const TREATMENT_STATUS_TO_TRIAGE_STAGE: Record<TreatmentStatusCode, TriageStage> = {
  triage: 'intake',
  'hsat-ordered': 'hsat',
  'in-lab-required': 'hsat',
  'hsat-delivered': 'hsat',
  'hsat-completed': 'hsat',
  'hsat-results-received': 'hsat-results',
  'first-appointment-scheduled': 'treatment-setup',
  'first-appointment-completed': 'treatment-setup',
  'followup-30': 'followup-30',
  'followup-90': 'followup-90',
  'followup-180': 'followup-180',
  'annual-followup': 'annual-followup',
  'non-adherent': 'non-adherent',
};

/**
 * Maps a treatment status code to its corresponding triage stage.
 * @param treatmentStatus - The granular treatment status code from the CarePlan
 * @returns The corresponding triage stage, or undefined if not mapped
 */
export function mapTreatmentStatusToTriageStage(
  treatmentStatus: TreatmentStatusCode | undefined
): TriageStage | undefined {
  if (!treatmentStatus) {
    return undefined;
  }
  return TREATMENT_STATUS_TO_TRIAGE_STAGE[treatmentStatus];
}

/**
 * Maps the Patient `meta.tag` triage status (POLARIS_TRIAGE_STATUS_TAG_SYSTEM) to
 * the high-level triage stage that gates self-scheduling.
 *
 * The tag is the source of truth: it's what the chart-header "Triage Status"
 * dropdown and the triage queues write, so reading it keeps self-scheduling in
 * lockstep with the status clinicians see and set. The CarePlan treatment-status
 * (mapTreatmentStatusToTriageStage) is a parallel representation that is not kept
 * in sync with the tag, which is why changing the header status never moved the
 * patient's schedulable appointments before.
 *
 * `undefined` means no self-schedulable appointments for that status — the patient
 * is in a staff-managed / hand-off state and the care team books for them.
 */
const PATIENT_TRIAGE_STATUS_TO_TRIAGE_STAGE: Record<PatientTriageStatus, TriageStage | undefined> = {
  [PATIENT_TRIAGE_STATUS.intake]: 'intake',
  [PATIENT_TRIAGE_STATUS.intakeAppointment]: 'intake',
  // Approved by Care Manager but HSAT not ordered yet — still in the Intake queue.
  [PATIENT_TRIAGE_STATUS.hsatApprovedCm]: 'intake',
  // Order placed by NP/PA — HSAT phase.
  [PATIENT_TRIAGE_STATUS.hsatApprovedNp]: 'hsat',
  [PATIENT_TRIAGE_STATUS.hsat]: 'hsat',
  [PATIENT_TRIAGE_STATUS.hsatOrdered]: 'hsat',
  // Results received, awaiting the results/diagnosis appointment (mirrors the
  // legacy hsat-results-received -> diagnosis mapping).
  [PATIENT_TRIAGE_STATUS.hsatResults]: 'hsat-results',
  [PATIENT_TRIAGE_STATUS.diagnosis]: 'hsat-results',
  [PATIENT_TRIAGE_STATUS.treatmentSetup]: 'treatment-setup',
  [PATIENT_TRIAGE_STATUS.followUp30Day]: 'followup-30',
  // The 60-plus DME follow-up appointment lives in the followup-30 stage bucket.
  [PATIENT_TRIAGE_STATUS.followUp60Day]: 'followup-30',
  [PATIENT_TRIAGE_STATUS.followUp90Day]: 'followup-90',
  // CONFIRM (clinical): no dedicated 150-day stage; routed to the next milestone (180).
  [PATIENT_TRIAGE_STATUS.followUp150Day]: 'followup-180',
  [PATIENT_TRIAGE_STATUS.followUp180Day]: 'followup-180',
  // CONFIRM (clinical): staff-managed states with no patient-self-schedulable type.
  [PATIENT_TRIAGE_STATUS.miscFollowUp]: undefined,
  [PATIENT_TRIAGE_STATUS.remoteMonitoring]: undefined,
  [PATIENT_TRIAGE_STATUS.annualFollowUp]: 'annual-followup',
  [PATIENT_TRIAGE_STATUS.nonAdherent]: 'non-adherent',
  // Denied / refer out — patient cannot self-schedule.
  [PATIENT_TRIAGE_STATUS.denied]: undefined,
};

/**
 * Maps a Patient triage-status tag code to its triage stage.
 * @param status - The triage status tag code (PATIENT_TRIAGE_STATUS value), or undefined
 * @returns The corresponding triage stage, or undefined if unset/unmapped
 */
export function mapPatientTriageStatusToTriageStage(status: string | undefined): TriageStage | undefined {
  if (!status) {
    return undefined;
  }
  return PATIENT_TRIAGE_STATUS_TO_TRIAGE_STAGE[status as PatientTriageStatus];
}

/**
 * Maps each triage stage to the appointment type codes the patient is allowed to schedule.
 * An empty array means scheduling is disabled for that stage.
 */
const TRIAGE_STAGE_TO_APPOINTMENT_TYPES: Record<TriageStage, string[]> = {
  intake: ['new-patient-intake'],
  hsat: ['check-in', 'follow-up', 'hsat-troubleshooting'],
  'hsat-results': ['follow-up', 'new-patient-results', 'existing-patient-results'],
  'treatment-setup': ['check-in', 'dme-setup-troubleshooting'],
  'followup-30': ['follow-up', 'dme-followup-30', 'dme-followup-60-plus'],
  'followup-90': ['follow-up', 'dme-followup-90'],
  'followup-180': ['follow-up', 'dme-followup-180'],
  'annual-followup': ['annual-followup'],
  'non-adherent': ['check-in', 'dme-setup-troubleshooting'],
};

/**
 * Gets the appointment type codes allowed for a given triage stage.
 * @param stage - The triage stage to look up
 * @returns Array of allowed appointment type codes (empty if scheduling is disabled)
 */
export function getAllowedAppointmentTypeCodes(stage: TriageStage | undefined): string[] {
  if (!stage) {
    return [];
  }
  return TRIAGE_STAGE_TO_APPOINTMENT_TYPES[stage];
}
